import React, {useState, FocusEvent, ChangeEvent, useCallback, useEffect} from 'react';
import {IconType, Icon} from '../Icon';
import {Text, TextVariants} from '../Text';
import {InputStateTypes} from '@/interface';
import {cn} from '@/utils/common_functions';
import {NA, nonDigitRegex} from '@/constants';
import Button from '../Button';
import { Tooltip } from '../Tooltip';

type inputTypes = 'text' | 'password' | 'numeric' | 'decimal';

interface TextInputProps {
  label?: string;
  value: string;
  name?: string;
  onChangeText?: (value: string) => void;
  disabled?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>) => void;
  error?: string;
  touched?: boolean;
  placeholder?: string;
  required?: boolean;
  leftIcon?: IconType;
  rightIcon?: IconType;
  className?: string;
  wrapperClassName?: string;
  iconClassName?: string;
  type?: inputTypes;
  multiline?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  max?: number;
  preventLeadingSpace?: boolean;
  preventTrailingSpace?: boolean;
  showOptionalLabel?: boolean;
  ignoredChars?: string[];
  allowedRegex?: RegExp;
  button?: {
    text: string;
    onClick: () => void;
    disabled: boolean;
    className?: string;
    textClassname?: string;
    isLoading?: boolean;
  };
  maxValue?: number;
  isHoverable?:boolean;
  disableAutofill?: boolean;
}

export const TextInput: React.FC<TextInputProps> = props => {
  const {
    value,
    className = '',
    disabled = false,
    error = '',
    iconClassName = '',
    label = '',
    name = '',
    onBlur,
    onChangeText,
    onFocus,
    placeholder = '',
    required = false,
    leftIcon,
    rightIcon,
    touched = false,
    type = 'text',
    wrapperClassName = '',
    multiline = false,
    labelClassName = '',
    inputClassName = '',
    max,
    button,
    preventLeadingSpace = false,
    preventTrailingSpace = false,
    showOptionalLabel = false,
    ignoredChars = [],
    allowedRegex = '',
    maxValue = null,
    isHoverable=false,
    disableAutofill=false,
  } = props;

  const [inputState, setInputState] = useState<InputStateTypes>('default');
  const maxLength = max ?? (multiline ? 500 : 60);
  /**
   * onChange handlers
   */
  function handleChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
    let text = e.target.value;
    if (preventLeadingSpace) {
      // Prevent leading spaces and collapse multiple spaces
      text = text.replace(/^\s+/, '');
    }
    if (preventTrailingSpace) {
      text = text.replace(/\s{2,}/g, ' ');
    }
    // Remove ignored characters
    if (ignoredChars && ignoredChars.length > 0) {
      const pattern = new RegExp(`[${ignoredChars.join('')}]`, 'g');
      text = text.replace(pattern, '');
    }

    // Apply allowed regex: keep only valid characters
    if (allowedRegex) {
      text = text
        .split('')
        .filter(char => allowedRegex.test(char))
        .join('');
    }

    if (type === 'numeric') {
      const numericValue = text.replace(nonDigitRegex, ''); // Remove non-numeric characters
      onChangeText?.(numericValue ?? null);
    } else if (type === 'decimal') {
      // Allow only valid decimal format
      const decimalValue = text.replace(/[^\d.]/g, ''); // Remove non-digit and non-dot

      // Prevent multiple decimal points
      const parts = decimalValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : decimalValue;

      onChangeText?.(formattedValue);
    } else {
      onChangeText?.(text);
    }
  }
  /**
   * a utily function to update input state with a check to disabled logic,
   */
  const updateInputState = useCallback(
    (state: InputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  /**
   * utility wrapper for onBlur handler, such that if also updates the input state locally
   */
  function onBlurHandler(e: FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>): void {
    updateInputState(error && touched ? 'error' : 'default');
    onBlur?.(e);
  }

  /**
   * utility wrapper for onFocus handler, such that if also updates the input state locally
   */
  function onFocusHandler(e: FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>): void {
    updateInputState('focus');
    onFocus?.(e);
  }

  useEffect(() => {
    if (touched && error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, touched]);

  /**
   * input props to pass here as common props
   */
  const inputProps = {
    onChange: handleChange,
    onFocus: onFocusHandler,
    onBlur: onBlurHandler,
    name,
    maxLength,
    className: cn(
      'px-3 py-2 lg:px-4 border-none outline-none bg-transparent grow w-full font-FigtreeMedium placeholder:text-ui_input_border_dark',
      disabled && 'text-ui_disabled cursor-not-allowed',
      multiline ? 'h-[100px] max-h-[100px] resize-none' : '',
      inputClassName,
    ),
    value,
    placeholder,
    disabled,
    autoComplete: disableAutofill ? 'off' : undefined,
  };

  const inputWrapperContent = (
    <div
      className={cn(
        'border-2 flex border-ui_input_border relative text-base rounded-lg overflow-hidden items-center disabled:cursor-not-allowed',
        inputState === 'error' && 'border-rejection',
        inputState === 'focus' && 'border-black',
        disabled && 'border-ui_disabled',
        TextVariants.body,
        wrapperClassName,
      )}>
      {leftIcon && !multiline ? <Icon type={leftIcon} className={cn('size-4', iconClassName)} /> : null}

      {multiline ? <textarea {...inputProps} /> : <input {...inputProps} />}

      {rightIcon && !multiline && button ? <Icon type={rightIcon} className={cn('size-4', iconClassName)} /> : null}

      {button && (
        <Button
          onClick={button.onClick}
          disabled={disabled || button.disabled}
          className={cn('h-full w-[130px] min-w-fit px-0 rounded-l-none', button.className)}
          title={button.text ?? ''}
          textClassName={cn('text-lg font-Medium', button.textClassname)}
          isLoading={button.isLoading}
        />
      )}
    </div>
  );

  return (
    <div className={cn('flex flex-col gap-1 lg:gap-2', className)}>
      {label && (
        <Text className={cn('!font-FigtreeSemiBold inline gap-1 text-ui_blue', labelClassName)} variant="body">
          {label}
          {required && <span className="text-rejection"> *</span>}
          {showOptionalLabel && <span className="font-FigtreeLight text-xs self-center mt-1"> (optional)</span>}
        </Text>
      )}
      
      {isHoverable ? (
        <Tooltip tooltipClassName='w-fit max-w-sm' message={value || NA}>
          {inputWrapperContent}
        </Tooltip>
      ) : (
        inputWrapperContent
      )}

      {!disabled && touched && error && <Text className="text-rejection text-sm">{error}</Text>}
    </div>
  );
};